import { useState } from 'react'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@agms/ui'
import { useCreateNote, useDeleteNote, useNotes } from '../../hooks/useNotes'
import { apiErrorMessage } from '../../hooks/useErrorHandler'
import type { AccountAnchor, AccountNote } from '../../lib/api'

interface Props {
  anchor: AccountAnchor
}

function formatTimestamp(value: string | null): string {
  if (!value) return ''
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function NoteRow({ note, anchor }: { note: AccountNote; anchor: AccountAnchor }) {
  const deleteNote = useDeleteNote()
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const author = note.author?.name ?? 'Migrated from lead notes'

  const handleDelete = () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true)
      return
    }
    deleteNote.mutate({ noteId: note.id, anchor }, {
      onSettled: () => setConfirmingDelete(false),
    })
  }

  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-800">{author}</p>
          <p className="text-xs text-slate-500">{formatTimestamp(note.created_at)}</p>
        </div>
        {note.can_delete && (
          <div className="flex items-center gap-2">
            {confirmingDelete && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setConfirmingDelete(false)}
              >
                Cancel
              </Button>
            )}
            <Button
              type="button"
              variant={confirmingDelete ? 'destructive' : 'ghost'}
              size="sm"
              onClick={handleDelete}
              isLoading={deleteNote.isPending}
              className={confirmingDelete ? undefined : 'text-red-600 hover:bg-red-50'}
            >
              {confirmingDelete ? 'Confirm delete' : 'Delete'}
            </Button>
          </div>
        )}
      </div>
      <p className="whitespace-pre-wrap text-sm leading-6 text-slate-700">{note.body}</p>
    </div>
  )
}

export function NotesPanel({ anchor }: Props) {
  const [page, setPage] = useState(1)
  const [body, setBody] = useState('')
  const [error, setError] = useState<string | null>(null)
  const notesQuery = useNotes(anchor, page)
  const createNote = useCreateNote()
  const trimmed = body.trim()
  const notes = notesQuery.data?.data ?? []
  const lastPage = notesQuery.data?.last_page ?? 1

  const submit = () => {
    setError(null)
    if (!trimmed) {
      setError('Enter a note before saving.')
      return
    }

    createNote.mutate(
      { anchor, payload: { body: trimmed } },
      {
        onSuccess: () => {
          setBody('')
          setPage(1)
        },
        onError: (err) => {
          setError(apiErrorMessage(err, 'Unable to save note.'))
        },
      },
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notes</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
          <label htmlFor={`${anchor.kind}-note-body`} className="mb-1 block text-sm font-medium text-slate-700">
            Add note
          </label>
          <textarea
            id={`${anchor.kind}-note-body`}
            value={body}
            onChange={(event) => {
              setBody(event.target.value.slice(0, 5000))
              setError(null)
            }}
            maxLength={5000}
            rows={4}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Add an internal note for this account..."
          />
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-slate-500">{body.length}/5000</span>
            <Button
              type="button"
              onClick={submit}
              disabled={!trimmed}
              isLoading={createNote.isPending}
            >
              Add Note
            </Button>
          </div>
          {error && <p className="mt-2 text-sm text-red-600" role="alert">{error}</p>}
        </div>

        {notesQuery.isLoading && (
          <p className="py-4 text-center text-sm text-slate-500">Loading notes...</p>
        )}

        {notesQuery.error && (
          <p className="py-4 text-center text-sm text-red-600" role="alert">Unable to load notes.</p>
        )}

        {!notesQuery.isLoading && !notesQuery.error && notes.length === 0 && (
          <p className="rounded-md border border-dashed border-slate-300 py-6 text-center text-sm text-slate-500">
            No notes yet.
          </p>
        )}

        {notes.length > 0 && (
          <div className="space-y-3">
            {notes.map((note) => (
              <NoteRow key={note.id} note={note} anchor={anchor} />
            ))}
          </div>
        )}

        {lastPage > 1 && (
          <div className="flex items-center justify-between border-t border-slate-200 pt-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1}
            >
              Previous
            </Button>
            <span className="text-xs text-slate-500">Page {page} of {lastPage}</span>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setPage((current) => Math.min(lastPage, current + 1))}
              disabled={page >= lastPage}
            >
              Next
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
